// ---------------------------------------------------------------------------
// The Sequence step's rules (R17b)
//
// How steps are numbered and labelled, what condition each one runs on, what
// a new step starts with, how a step is inserted, removed or moved, what the
// step warns about and what it refuses, and what a step's preview is asked
// with. Taken out of wizard/wiz-sequence.ts unchanged, so the step's rebuild
// (R17m–n) redoes only the markup. Plain TypeScript.
// ---------------------------------------------------------------------------

import type { DelayUnit, OfferingConfig, SequenceChannel, SequenceStep } from '../types.js';
import {
  buildPreviewRequestBody,
  DEFAULT_PROMPTS,
  SEQUENCE_NEEDS_EMAIL_ACCOUNT,
  SEQUENCE_NEEDS_LINKEDIN_ACCOUNT,
} from '../types.js';
import type { LeadRecord } from './leads.js';

// Kept in sync with LINKEDIN_CONNECT_NOTE_MAX_CHARS in
// entrypoints/sdr/src/server/helpers/message-generation.ts (the server-side
// source of truth — a different package, so it can't be imported directly).
// Live testing showed Unipile rejects connection-request invites with HTTP
// 400 errors/too_many_characters even at exactly 300 chars (a figure widely
// quoted for LinkedIn's web UI) — 200 is Unipile's own documented cap.
export const LINKEDIN_CONNECT_NOTE_MAX_CHARS = 200;

/** The two lanes of the sequence: email steps on one side, LinkedIn steps on the other. */
export type Lane = 'email' | 'linkedin';

export const laneOf = (c: SequenceChannel): Lane => (c === 'email' ? 'email' : 'linkedin');

const ORDINAL_SUFFIX = ['th', 'st', 'nd', 'rd'];
/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th"… */
export const ordinal = (n: number): string => {
  const v = n % 100;
  return `${n}${ORDINAL_SUFFIX[(v - 20) % 10] ?? ORDINAL_SUFFIX[v] ?? ORDINAL_SUFFIX[0]}`;
};

export interface StepDescriptor {
  /** 1-based position within the step's own lane (Email #1,#2 … / LinkedIn #1,#2 …). */
  number: number;
  /** Position-aware chip label: "Intro Email", "1st Follow-up", "Connect", "Message". */
  label: string;
}

/** Per-lane numbering + AiSDR-style position labels for every step. */
export function describeSteps(seq: SequenceStep[]): StepDescriptor[] {
  let e = 0;
  let l = 0;
  return seq.map(s => {
    if (laneOf(s.channel) === 'email') {
      const n = e++;
      return { number: n + 1, label: n === 0 ? 'Intro Email' : `${ordinal(n)} Follow-up` };
    }
    const n = l++;
    return { number: n + 1, label: s.channel === 'linkedin_connect' ? 'Connect' : 'Message' };
  });
}

/** Singular/plural-aware unit word for the “Wait …” chip. */
export const unitWord = (v: number, u: DelayUnit): string => (v === 1 ? u.replace(/s$/, '') : u);

/**
 * Conditions are derived automatically (no per-step UI):
 *  • the very first step always runs ("always");
 *  • a LinkedIn DM needs an accepted connection first ("accepted");
 *  • every other follow-up only runs if the lead has NOT replied yet ("no_reply").
 * A reply on any channel stops the rest of the sequence, so "no_reply" is the
 * natural guard for everything after step 1.
 */
export function normalizeConditions(steps: SequenceStep[]): SequenceStep[] {
  return steps.map((s, i) => {
    const condition: SequenceStep['condition'] =
      i === 0 ? 'always' : s.channel === 'linkedin_dm' ? 'accepted' : 'no_reply';
    return s.condition === condition ? s : { ...s, condition };
  });
}

/**
 * A new step for `channel` at `pos`: the default prompt (an email after another
 * email is a follow-up), the default subject for email, and the default wait
 * (none for the first step, 15 minutes before a DM, 2 days otherwise). Its
 * condition is a placeholder: the sequence re-derives it (normalizeConditions).
 */
export function newStep(channel: SequenceChannel, pos: number, sequence: SequenceStep[], id: string): SequenceStep {
  const isFollowup = channel === 'email' && sequence.some(s => s.channel === 'email');
  const promptKey = channel === 'email' ? (isFollowup ? 'email_followup' : 'email') : channel;
  return {
    id,
    channel,
    delayValue: pos === 0 ? 0 : channel === 'linkedin_dm' ? 15 : 2,
    delayUnit: pos === 0 ? 'hours' : channel === 'linkedin_dm' ? 'minutes' : 'days',
    condition: 'no_reply',
    prompt: DEFAULT_PROMPTS[promptKey] ?? '',
    subject: channel === 'email' ? 'Quick question about {{company}}' : '',
  };
}

/** The sequence with `step` inserted at `pos`. */
export const withStepInserted = (sequence: SequenceStep[], pos: number, step: SequenceStep): SequenceStep[] => {
  const arr = [...sequence];
  arr.splice(pos, 0, step);
  return arr;
};

/** The sequence without the step at `idx`, and which step stays open. */
export const withoutStep = (
  sequence: SequenceStep[],
  idx: number,
  expanded: number | null,
): { sequence: SequenceStep[]; expanded: number | null } => ({
  sequence: sequence.filter((_, i) => i !== idx),
  expanded: expanded === idx ? null : expanded !== null && expanded > idx ? expanded - 1 : expanded,
});

/** The sequence with the step at `from` moved to `to`, or null when `to` is outside it. */
export const withStepMoved = (sequence: SequenceStep[], from: number, to: number): SequenceStep[] | null => {
  if (to < 0 || to >= sequence.length) return null;
  const arr = [...sequence];
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item!);
  return arr;
};

/** The warnings over the sequence: a delay on step 1, a DM before a connect, "Accepted" without one, several "Always". */
export function sequenceWarnings(sequence: SequenceStep[]): string[] {
  const w: string[] = [];
  if (sequence.length === 0) return w;

  if (sequence[0]!.delayValue > 0) {
    w.push('Step 1 has a delay — typically the first step starts immediately (delay 0).');
  }

  let hasConnect = false;
  for (const s of sequence) {
    if (s.channel === 'linkedin_connect') hasConnect = true;
    if (s.channel === 'linkedin_dm' && !hasConnect) {
      w.push('LinkedIn DM before a Connect step — the lead may not be connected yet.');
      break;
    }
  }

  for (let i = 0; i < sequence.length; i++) {
    if (sequence[i]!.condition === 'accepted') {
      const hasPriorConnect = sequence.slice(0, i).some(s => s.channel === 'linkedin_connect');
      if (!hasPriorConnect) {
        w.push(`Step ${i + 1} has "Accepted" condition but no prior LinkedIn Connect step.`);
      }
    }
  }

  // Multiple "Always" steps: once a lead replies on ANY channel the whole
  // outbound sequence stops automatically (there is no "if replied" step), so
  // a later "Always" step — e.g. an "Always" LinkedIn DM after an "Always"
  // email — will be SKIPPED for any lead who already replied. Warn so the user
  // knows the second "Always" only reaches non-repliers.
  const alwaysSteps = sequence.map((s, i) => ({ step: s, idx: i })).filter(({ step }) => step.condition === 'always');
  if (alwaysSteps.length > 1) {
    const stepList = alwaysSteps.map(s => s.idx + 1).join(', ');
    w.push(
      `Multiple "Always" steps (steps ${stepList}). A reply on any channel stops the rest of the ` +
        `sequence, so later "Always" steps are skipped for leads who already replied — they only ` +
        `reach leads who have NOT replied yet.`,
    );
  }

  return w;
}

/**
 * Hard blockers: a channel is used in the sequence but no matching account is
 * connected for the assigned Sales Representative(s). They render as errors.
 */
export function sequenceAccountErrors(
  sequence: SequenceStep[],
  hasEmailAccount: boolean,
  hasLinkedinAccount: boolean,
): string[] {
  const e: string[] = [];
  const hasEmailStep = sequence.some(s => s.channel === 'email');
  const hasLinkedinStep = sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
  if (hasEmailStep && !hasEmailAccount) e.push(SEQUENCE_NEEDS_EMAIL_ACCOUNT);
  if (hasLinkedinStep && !hasLinkedinAccount) e.push(SEQUENCE_NEEDS_LINKEDIN_ACCOUNT);
  return e;
}

/** Minimal lead facts used to personalize the step-card preview. */
export interface SampleLead {
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  industry?: string;
  email?: string;
}

/** The first uploaded or imported lead, as the sample a preview is written for; none without leads. */
export const uploadedSampleLead = (leads: LeadRecord[]): SampleLead | null =>
  leads.length > 0
    ? {
        firstName: leads[0]?.firstName,
        lastName: leads[0]?.lastName,
        company: leads[0]?.company,
        jobTitle: leads[0]?.jobTitle,
        industry: leads[0]?.industry,
      }
    : null;

/** The sample lead from the search's first lead, when the campaign sources instead of uploading. */
// biome-ignore lint/suspicious/noExplicitAny: a lead as POST /api/apollo/search answers it
export const sourcedSampleLead = (lead: any): SampleLead | null =>
  lead
    ? {
        firstName: lead.firstName,
        lastName: lead.lastName,
        company: lead.company,
        jobTitle: lead.jobTitle,
        industry: lead.industry,
      }
    : null;

// Generated samples are persisted on the step itself (previewSubject /
// previewBody / previewKey) so the card survives collapsing, wizard
// navigation and re-opening the campaign without a fresh LLM call.
// `previewKey` fingerprints the content the sample was generated from. Note
// (R17b review): nothing reads it back. The card serves a stored sample as it
// is, also after the prompt or the offering changed, until the person asks for
// a new one (Generate, Shorten, Make it friendly, Regenerate); a language
// switch drops the stored samples (withLanguage in draft.ts).
export const contentHash = (value: string): string => {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

export const previewContentKey = (step: SequenceStep, offering: OfferingConfig): string =>
  contentHash(
    JSON.stringify([
      step.prompt,
      step.subject,
      step.subjectPrompt ?? '',
      step.channel,
      offering.name,
      offering.description,
      offering.talkingPoints,
    ]),
  );

/**
 * The POST /api/prompt/test body for a step's preview, or null for a step
 * without a prompt (its preview is empty). A refine instruction typed under the
 * preview keeps steering every regeneration (language switches included), not
 * just the Generate click that rewrote the prompt. The preview is personalized
 * with a real lead; an obfuscated Apollo last name ("Bo***n") is left out.
 */
export function previewRequest(
  step: SequenceStep,
  offering: OfferingConfig,
  sampleLead?: SampleLead | null,
  language?: string,
  aiEmployeeId?: string,
): Record<string, unknown> | null {
  if (!step.prompt.trim()) return null;
  const instruction = step.refineInstruction?.trim();
  const effectivePrompt = instruction
    ? `${step.prompt}\n\nADDITIONAL INSTRUCTION (must be followed): ${instruction}`
    : step.prompt;
  const lead: Record<string, string> = {};
  if (sampleLead) {
    if (sampleLead.firstName) lead.first_name = sampleLead.firstName;
    if (sampleLead.lastName && !sampleLead.lastName.includes('*')) lead.last_name = sampleLead.lastName;
    if (sampleLead.company) lead.company = sampleLead.company;
    if (sampleLead.jobTitle) lead.job_title = sampleLead.jobTitle;
    if (sampleLead.industry) lead.industry = sampleLead.industry;
    if (sampleLead.email) lead.email = sampleLead.email;
  }
  return buildPreviewRequestBody({ step, offering, effectivePrompt, lead, language, aiEmployeeId });
}

/** What an AI action under a preview rewrites: the message prompt, or (mode `subject`) the subject. */
export type ImproveMode = 'shorten' | 'friendly' | 'instruction' | 'subject';

/** The POST /api/prompt/improve body. */
export const improveRequest = (step: SequenceStep, mode: ImproveMode, freeInstruction?: string) => ({
  text: mode === 'subject' ? step.subject : step.prompt,
  mode,
  instruction: mode === 'subject' ? step.prompt : freeInstruction,
  channel: step.channel,
});
